import {
    call,
    put,
    select,
} from 'redux-saga/effects';
import {
    getCaptchaToken,
    listenForCaptchaVerify,
    resetCaptcha,
} from '../../../components/mtCaptcha';
import {
    setOverlayContent,
    setOverlayVisibility,
    preventCancel,
    startLoadingOverlay,
    stopLoadingOverlay,
} from '../../Overlay/action';
import { OverlayContent } from '../../Overlay/enum';
import {
    Address,
    setAddressCookie,
} from '@vfde-sails/avc-core';
import {
    getAlternativeAvailability,
    getValidAddresses,
} from '../../../util/jarvis';
import {
    JarvisAlternativeResponse,
    JarvisAvcSuccessResponse,
} from '../../../interface';
import {
    Actions,
    setAddresses,
    setError,
} from '../action';
import {
    setAvailability,
    startLoading,
    stopLoading,
} from '../../App/action';
import {
    isAddressNotFoundError,
    isCaptchaError,
} from '../../../util/typeGuard';
import { trackPageViewSaga } from '../../App/util/generator';
import {
    selectAppState,
    selectIsLoggedInCableCustomer,
} from '../../App/selector';
import {
    trackAvcForm,
    trackCaptcha,
    trackError,
} from '../../../tracking/tracking';
import { AppState } from '../../App/interface';
import { TrackingFormStepName } from '../../../tracking/enum';
import { getAddressFromForm } from './converter';
import { setCookie } from '@vfde-sails/utils';
import { AddressForm } from '../interface';
import { ErrorType } from '../../App/enum';

/**
 * Show captcha to user and verify input
 */
export function* verifyCaptcha (action: Actions, isSeamless: boolean) {
    yield call(resetCaptcha);

    if (getCaptchaToken()) {
        return;
    }

    yield call(trackCaptcha);

    yield put(setOverlayVisibility(true, OverlayContent.CAPTCHA));
    yield put(stopLoading(action.type));
    yield put(stopLoadingOverlay(action.type));

    yield call(listenForCaptchaVerify);

    if (isSeamless) {
        yield put(preventCancel());
        yield put(setOverlayVisibility(false, OverlayContent.CAPTCHA));
    }
    else {
        yield put(setOverlayContent(OverlayContent.AVC));
    }

    yield put(startLoading(action.type));
    yield put(startLoadingOverlay(action.type));
}

/**
 * Checks for alternative tariff if none of cable tariffs are available
 */
export function* checkAlternativeAvailability (action: Actions, address: Address, zpApiState: string | undefined, isSeamless: boolean) {
    try {

        const alternativeAvailability: JarvisAlternativeResponse | undefined = yield call(
            getAlternativeAvailability,
            address,
            getCaptchaToken(),
            zpApiState,
        );

        return alternativeAvailability;
    }
    catch (error) {
        if (!isCaptchaError(error)) {
            throw error;
        }

        yield call(verifyCaptcha, action, isSeamless);

        const alternativeAvailability: JarvisAlternativeResponse | undefined = yield call(
            getAlternativeAvailability,
            address,
            getCaptchaToken(),
            zpApiState,
        );

        return alternativeAvailability;
    }
}

/**
 * Handle success response
 */
export function* handleSuccessResponse (
    action: Actions,
    response: JarvisAvcSuccessResponse,
    { product, availability }: JarvisAlternativeResponse,
    isSeamless: boolean,
) {
    yield put(setAddresses([response.address]));

    const alternativeTariff = !response.products.length ? product : undefined;

    yield put(setAvailability(response, availability, alternativeTariff));

    const isLoggedInCableCustomer: boolean = yield select(selectIsLoggedInCableCustomer);

    if (isLoggedInCableCustomer) {
        yield call(trackPageViewSaga);
    }
    else {
        yield call(setAddressCookie, response.address);
    }

    yield call(trackAvcFormSaga, TrackingFormStepName.COMPLETE, isSeamless);
    yield put(setOverlayContent(OverlayContent.AVC_RESULT));
}

/**
 * Handle not unique address response
 */
export function* handleAddressError (addresses: Address[], error: Error, type: ErrorType) {
    yield put(setAddresses(addresses));
    yield put(setError({ error, type }));
    yield call(trackError, error, type);
}

/**
 * Validate address or get logged in address
 */
export function* validateAddress (
    addressForm: AddressForm,
    selectedAddress: Address | undefined,
    isLoggedInCableCustomer: boolean,
    gimliState?: string,
) {
    try {
        const address: Partial<Address> | undefined = !isLoggedInCableCustomer
            ? selectedAddress ?? (yield call(getAddressFromForm, addressForm))
            : undefined;
        const addresses: Address[] = yield call(getValidAddresses, isLoggedInCableCustomer, address, gimliState);

        if (addresses.length > 1) {
            yield call(handleAddressError, addresses, new Error('Not a unique address'), ErrorType.NOT_UNIQUE_ADDRESS);

            return;
        }

        return addresses[0];
    }
    catch (error) {
        if (isAddressNotFoundError(error)) {
            yield call(handleAddressError, [], error, ErrorType.NOT_FOUND_ADDRESS);

            return;
        }

        throw error;
    }
}

/**
 * Tracks avc form by step
 */
export function* trackAvcFormSaga (step: TrackingFormStepName, isSeamless = false) {
    if (isSeamless) {
        return;
    }

    const state: AppState = yield select(selectAppState);
    yield call(trackAvcForm, state, step);
}
